/**
 * Proof shown on the public pages. Everything here is a statement about real people, so each part
 * stays EMPTY — and its section stays hidden — until the real thing exists:
 *
 * - `CLINICAL_LEAD`: the clinician's own details and their own signed statement. Fill it only
 *   with wording they wrote or approved, and their registration exactly as on the council's
 *   register. Leave `statement` empty while the report's content is still marked provisional.
 * - `TESTIMONIALS`: words a parent actually sent us, used with their written permission
 *   (`consentOn`). Never a made-up or paraphrased quote, and never a child's name: invented
 *   reviews are an unfair trade practice under the Consumer Protection Act, 2019, and the page
 *   says nothing it cannot show.
 */

export interface ClinicalLead {
  name: string;
  /** e.g. "MD (Paediatrics)"; as the clinician writes it. */
  qualifications: string;
  /** e.g. "Developmental paediatrician, 14 years". */
  role: string;
  /** Council and number, e.g. "Delhi Medical Council 12345". */
  registration: string;
  /** Their statement about the report, in their words, in each language. Empty = not shown. */
  statement: { en: string; hi: string };
  /** ISO date the statement was signed. */
  signedOn: string;
  /** Optional portrait, imported from `src/assets`. */
  photo?: string;
}

export const CLINICAL_LEAD: ClinicalLead | null = null;

export interface Testimonial {
  /** The parent's words as sent; pick the language they wrote in and translate the other faithfully. */
  quote: { en: string; hi: string };
  /** How they asked to be named, e.g. "Mother of a 4-year-old". First name only if they agreed. */
  by: { en: string; hi: string };
  city?: string;
  /** ISO date they agreed in writing to be quoted. Required. */
  consentOn: string;
}

export const TESTIMONIALS: Testimonial[] = [];

export const publishableLead = (lead: ClinicalLead | null): ClinicalLead | null =>
  lead && lead.name.trim() && lead.registration.trim() && lead.statement.en.trim() && lead.statement.hi.trim() && lead.signedOn ? lead : null;

export const publishableTestimonials = (items: Testimonial[]): Testimonial[] =>
  items.filter((item) => item.quote.en.trim() && item.quote.hi.trim() && item.by.en.trim() && /^\d{4}-\d{2}-\d{2}/.test(item.consentOn));
